import {
  DSBadge,
  DSButton,
  DSCardWrapper,
  DSInput,
} from "@components/design-system/molecules";
import { useCurrentUser, useOrganization } from "@hooks";
import { useRecordingConsentEvents } from "@hooks/use-recording-consent-events";
import { useEffect, useState } from "react";

import {
  consentEventsToCsv,
  dateFilterToIso,
  formatConsentTimestamp,
} from "./recording-consent-utils";
import { downloadCsv } from "../../utils/csv";
import { alert } from "../common/Alert";

export function RecordingConsentSection() {
  const { orgUuid, isOrgAdmin } = useOrganization();
  const { user } = useCurrentUser();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appliedDates, setAppliedDates] = useState({
    dateFrom: undefined as string | undefined,
    dateTo: undefined as string | undefined,
  });
  const [exporting, setExporting] = useState(false);
  const {
    events,
    nextCursor,
    loading,
    error,
    loadMore,
    getAllFilteredEvents,
  } = useRecordingConsentEvents(orgUuid, appliedDates, !isOrgAdmin);

  // Sticky: once ANY events have loaded for this org, the section stays
  // visible. Without this, an org whose consent flag is now off but which has
  // history could apply a date filter matching nothing and watch the whole
  // section — filter controls included — vanish with no way back.
  const [hasLoadedEvents, setHasLoadedEvents] = useState(false);
  useEffect(() => {
    if (events.length > 0) {
      setHasLoadedEvents(true);
    }
  }, [events.length]);

  if (!isOrgAdmin) return null;
  if (
    !loading &&
    !error &&
    user?.requiresRecordingConsent !== true &&
    !hasLoadedEvents &&
    events.length === 0
  ) {
    return null;
  }

  const applyDateFilter = () => {
    setAppliedDates({
      dateFrom: dateFilterToIso(dateFrom),
      dateTo: dateFilterToIso(dateTo, true),
    });
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const filteredEvents = await getAllFilteredEvents();
      downloadCsv(
        `recording-consent-${new Date().toISOString().slice(0, 10)}.csv`,
        consentEventsToCsv(filteredEvents),
      );
    } catch {
      // The export paginates across the org's full history — any page fetch
      // can fail. Without this catch that was an unhandled rejection with
      // zero user feedback.
      alert(
        "error",
        "The consent CSV export failed. Please try again.",
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <section
      className="mt-xl"
      data-testid="recording-consent-section"
    >
      <div className="mb-md flex flex-wrap items-end justify-between gap-md">
        <div>
          <h2>Recording Consent</h2>
          <p className="text-secondary">
            Recording consent activity for your organization.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-sm">
          <DSInput
            dataTestId="consent-date-from"
            label="From"
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(event) => setDateFrom(event.target.value)}
          />
          <DSInput
            dataTestId="consent-date-to"
            label="To"
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(event) => setDateTo(event.target.value)}
          />
          <DSButton
            dataTestId="consent-apply-filter"
            variant="secondary"
            onClick={applyDateFilter}
            disabled={loading}
          >
            Filter
          </DSButton>
          <DSButton
            dataTestId="consent-export-csv"
            onClick={exportCsv}
            disabled={loading || exporting}
          >
            {exporting ? "Exporting..." : "Export CSV"}
          </DSButton>
        </div>
      </div>

      {error && <p className="mb-sm text-danger">{error.message}</p>}

      <DSCardWrapper
        dataTestId="recording-consent-table"
        padding="p-sm"
      >
        {loading && events.length === 0 ? (
          <p className="p-md text-secondary">
            Loading consent events...
          </p>
        ) : events.length === 0 ? (
          <p className="p-md text-secondary">
            No consent events found.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-primary">
              <thead className="text-secondary">
                <tr className="border-b border-primary">
                  {[
                    "User email",
                    "Action",
                    "Surface",
                    "Consent text",
                    "Version",
                    "Source",
                    "Note",
                    "Received",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="px-sm py-sm font-semibold"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr
                    key={event.uuid}
                    className="border-b border-secondary"
                  >
                    <td className="px-sm py-sm">{event.userEmail}</td>
                    <td className="px-sm py-sm">
                      <DSBadge
                        dataTestId={`consent-action-${event.uuid}`}
                        variant={
                          event.action === "ACKNOWLEDGED"
                            ? "green"
                            : "red"
                        }
                        size="sm"
                      >
                        {event.action}
                      </DSBadge>
                    </td>
                    <td className="px-sm py-sm">{event.surface}</td>
                    <td className="px-sm py-sm whitespace-pre-line">
                      {event.consentText}
                    </td>
                    <td className="px-sm py-sm whitespace-nowrap">
                      {event.consentTextVersion}
                    </td>
                    <td className="px-sm py-sm">{event.source}</td>
                    <td className="px-sm py-sm">
                      {event.noteUuid ? (
                        <a
                          className="text-brand"
                          href={`/edit_note/${event.noteUuid}`}
                        >
                          View note
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-sm py-sm whitespace-nowrap">
                      {formatConsentTimestamp(event.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DSCardWrapper>

      {nextCursor && (
        <div className="mt-md flex justify-center">
          <DSButton
            dataTestId="consent-load-more"
            variant="secondary"
            onClick={loadMore}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load more"}
          </DSButton>
        </div>
      )}
    </section>
  );
}
